import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, TextField, MenuItem,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Alert, Table, TableBody, TableCell, TableHead, TableRow,
  TablePagination, Tooltip, Tabs, Tab, LinearProgress, Divider,
} from '@mui/material';
import {
  Key, Add, Refresh, ContentCopy, Delete, Edit, CheckCircle, Cancel,
  Visibility, VisibilityOff, BarChart, Assessment, TrendingUp,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface ApiKeyData {
  id: string;
  apiKey: string;
  nome: string;
  entidadeId: string | null;
  userId: string | null;
  entidadeNome: string;
  plano: string;
  rateLimit: number;
  dailyLimit: number;
  monthlyLimit: number;
  estado: string;
  permissoes: string;
  dtCriacao: string;
  dtExpiracao: string | null;
  ultimoUso: string | null;
}

interface UsageData {
  total: number;
  today: number;
  thisMonth: number;
  daily: { date: string; requests: number; avgResponseTime: number }[];
  byEndpoint: { endpoint: string; method: string; requests: number }[];
  byStatus: { statusCode: number; requests: number }[];
}

interface BillingSummary {
  apiKeyId: string;
  keyName: string;
  plan: string;
  entityId: string;
  entityName: string;
  totalRequests: number;
  avgResponseTime: number;
  lastRequest: string | null;
  limits: { rateLimit: number; dailyLimit: number; monthlyLimit: number };
}

const PLAN_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  free: 'default', basic: 'info', pro: 'warning', enterprise: 'success',
};

const ApiKeys: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'PFADM';
  const isDev = user?.role === 'PFDEV';
  const [activeTab, setActiveTab] = useState(0);
  const [keys, setKeys] = useState<ApiKeyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, activas: 0, suspensas: 0, expiradas: 0 });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [entities, setEntities] = useState<any[]>([]);
  const [createForm, setCreateForm] = useState({ nome: '', entidadeId: '', plano: 'free' });
  const [saving, setSaving] = useState(false);
  const [generatingDevKey, setGeneratingDevKey] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState<ApiKeyData | null>(null);
  const [editForm, setEditForm] = useState({ nome: '', plano: '', estado: '' });

  // Credentials dialog (after create)
  const [credOpen, setCredOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');

  // Usage/Billing
  const [usagePeriod, setUsagePeriod] = useState('30');
  const [selectedKeyUsage, setSelectedKeyUsage] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);

  // Feedback
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Visibility
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1), limit: String(rowsPerPage),
        ...(searchQuery && { search: searchQuery }),
      });
      const response = await api.get(`/admin/api-keys?${params}`);
      if (response.data.success) {
        setKeys(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
        if (response.data.stats) setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery]);

  const fetchEntities = async () => {
    try {
      const response = await api.get('/admin/entities?limit=200&status=A');
      if (response.data.success) setEntities(response.data.data || []);
    } catch (err) {
      console.error('Error fetching entities:', err);
    }
  };

  const fetchUsage = useCallback(async (keyId: string) => {
    setUsageLoading(true);
    try {
      const response = await api.get(`/admin/api-keys/${keyId}/usage?period=${usagePeriod}`);
      if (response.data.success) {
        setUsageData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
      setUsageData(null);
    } finally {
      setUsageLoading(false);
    }
  }, [usagePeriod]);

  const fetchBillingSummary = useCallback(async () => {
    setUsageLoading(true);
    try {
      const response = await api.get(`/admin/api-keys/billing/summary?period=${usagePeriod}`);
      if (response.data.success) {
        setBillingSummary(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching billing:', err);
      setBillingSummary([]);
    } finally {
      setUsageLoading(false);
    }
  }, [usagePeriod]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  useEffect(() => {
    if (activeTab === 1) {
      if (isAdmin) {
        fetchBillingSummary();
      } else if (keys.length > 0 && !selectedKeyUsage) {
        setSelectedKeyUsage(keys[0].id);
      }
    }
  }, [activeTab, isAdmin, keys, fetchBillingSummary, selectedKeyUsage]);

  useEffect(() => {
    if (activeTab === 1 && selectedKeyUsage) {
      fetchUsage(selectedKeyUsage);
    }
  }, [activeTab, selectedKeyUsage, fetchUsage]);

  const handleGenerateDevKey = async () => {
    if (!window.confirm('Gerar nova API Key? A key anterior deixará de funcionar.')) return;
    setGeneratingDevKey(true);
    setErrorMsg('');
    try {
      const response = await api.post('/admin/api-keys/my-key');
      setNewApiKey(response.data.data.apiKey);
      setCredOpen(true);
      fetchKeys();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Erro ao gerar API Key');
    } finally {
      setGeneratingDevKey(false);
    }
  };

  const handleCreate = () => {
    setCreateForm({ nome: '', entidadeId: '', plano: 'free' });
    fetchEntities();
    setCreateOpen(true);
  };

  const handleSaveCreate = async () => {
    if (!createForm.nome || !createForm.entidadeId) {
      setErrorMsg('Nome e entidade são obrigatórios');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      const response = await api.post('/admin/api-keys', createForm);
      setCreateOpen(false);
      setNewApiKey(response.data.data.apiKey);
      setCredOpen(true);
      fetchKeys();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Erro ao criar API key');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (key: ApiKeyData) => {
    setEditKey(key);
    setEditForm({ nome: key.nome, plano: key.plano, estado: key.estado });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editKey) return;
    setSaving(true);
    try {
      await api.put(`/admin/api-keys/${editKey.id}`, editForm);
      setSuccessMsg('API Key atualizada');
      setEditOpen(false);
      fetchKeys();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: ApiKeyData) => {
    if (!window.confirm(`Revogar a API key "${key.nome}"? Esta acção é irreversível.`)) return;
    try {
      await api.delete(`/admin/api-keys/${key.id}`);
      setSuccessMsg('API Key revogada');
      fetchKeys();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message);
    }
  };

  const maskKey = (key: string) => `${key.substring(0, 12)}...${key.substring(key.length - 6)}`;

  const toggleKeyVisibility = (id: string) => {
    const next = new Set(visibleKeys);
    if (next.has(id)) next.delete(id); else next.add(id);
    setVisibleKeys(next);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMsg('Copiado para a área de transferência');
  };

  const formatNumber = (n: number) => n.toLocaleString('pt-PT');

  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'success';
    if (code >= 400 && code < 500) return 'warning';
    if (code >= 500) return 'error';
    return 'default';
  };

  // Max value for bar chart simulation
  const maxDailyRequests = usageData?.daily?.length
    ? Math.max(...usageData.daily.map(d => d.requests), 1)
    : 1;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>API Keys</Typography>
          <Typography variant="body1" color="text.secondary">
            Gestão de chaves de acesso e monitorização de uso
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin && <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Nova API Key</Button>}
          {isDev && (
            <Button variant="contained" startIcon={keys.length > 0 ? <Refresh /> : <Add />}
              onClick={handleGenerateDevKey} disabled={generatingDevKey}>
              {generatingDevKey ? 'A gerar...' : keys.length > 0 ? 'Regenerar Key' : 'Gerar API Key'}
            </Button>
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { fetchKeys(); if (activeTab === 1) { isAdmin ? fetchBillingSummary() : selectedKeyUsage && fetchUsage(selectedKeyUsage); } }}>Atualizar</Button>
        </Box>
      </Box>

      {/* Alerts */}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      {/* Developer sandbox notice */}
      {isDev && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Ambiente Sandbox</strong> — A sua API Key é pessoal e serve para testar integrações.
          Use o header <code>X-API-Key: &lt;sua-key&gt;</code> nos pedidos à API Pagali.
          Keys sandbox começam com <code>pk_test_</code>.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab icon={<Key />} label="Chaves" iconPosition="start" />
          <Tab icon={<BarChart />} label="Uso & Billing" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB 0: Keys */}
      {activeTab === 0 && (
        <>
          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              { label: 'Total Keys', value: stats.total, icon: <Key />, color: 'primary' },
              { label: 'Activas', value: stats.activas, icon: <CheckCircle />, color: 'success' },
              { label: 'Suspensas', value: stats.suspensas, icon: <Cancel />, color: 'warning' },
              { label: 'Expiradas', value: stats.expiradas, icon: <Cancel />, color: 'error' },
            ].map((stat) => (
              <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${stat.color}.light`, color: `${stat.color}.main` }}>
                        {stat.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                        <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Search */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <TextField fullWidth size="small" label="Pesquisar" placeholder="Nome, key..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </Paper>

          {/* Table */}
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Entidade</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell align="center">Rate Limit</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell>Último Uso</TableCell>
                  {isAdmin && <TableCell align="right">Acções</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>A carregar...</TableCell></TableRow>
                ) : keys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      {isDev
                        ? <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Ainda não tem uma API Key de sandbox.</Typography>
                            <Button variant="contained" startIcon={<Add />} onClick={handleGenerateDevKey} disabled={generatingDevKey}>
                              {generatingDevKey ? 'A gerar...' : 'Gerar API Key'}
                            </Button>
                          </Box>
                        : 'Nenhuma API key encontrada'
                      }
                    </TableCell>
                  </TableRow>
                ) : keys.map((key) => (
                  <TableRow key={key.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelectedKeyUsage(key.id); setActiveTab(1); }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{key.nome}</Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                          {visibleKeys.has(key.id) ? key.apiKey : maskKey(key.apiKey)}
                        </Typography>
                        <IconButton size="small" onClick={() => toggleKeyVisibility(key.id)}>
                          {visibleKeys.has(key.id) ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                        <Tooltip title="Copiar">
                          <IconButton size="small" onClick={() => copyToClipboard(key.apiKey)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {key.userId && !key.entidadeId
                        ? <Chip label="Developer" size="small" variant="outlined" color="secondary" />
                        : key.entidadeNome || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={key.plano.toUpperCase()} size="small" color={PLAN_COLORS[key.plano] || 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">{key.rateLimit}/min</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={key.estado} size="small"
                        color={key.estado === 'active' ? 'success' : key.estado === 'suspended' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {key.ultimoUso ? new Date(key.ultimoUso).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : 'Nunca'}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleOpenEdit(key)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(key)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={totalCount} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              labelRowsPerPage="Linhas:"
            />
          </Paper>
        </>
      )}

      {/* TAB 1: Usage & Billing */}
      {activeTab === 1 && (
        <>
          {/* Period selector */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField select size="small" label="Período" value={usagePeriod}
              onChange={(e) => setUsagePeriod(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="7">Últimos 7 dias</MenuItem>
              <MenuItem value="30">Últimos 30 dias</MenuItem>
              <MenuItem value="90">Últimos 90 dias</MenuItem>
            </TextField>
            {!isAdmin && keys.length > 1 && (
              <TextField select size="small" label="API Key" value={selectedKeyUsage || ''}
                onChange={(e) => setSelectedKeyUsage(e.target.value)} sx={{ minWidth: 250 }}>
                {keys.map(k => <MenuItem key={k.id} value={k.id}>{k.nome}</MenuItem>)}
              </TextField>
            )}
          </Box>

          {usageLoading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Admin: Billing Summary */}
          {isAdmin && (
            <Paper sx={{ mb: 4 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  <Assessment sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Resumo de Billing por Entidade
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entidade</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Resp. Médio</TableCell>
                    <TableCell align="right">Limite Diário</TableCell>
                    <TableCell align="right">Limite Mensal</TableCell>
                    <TableCell>Último Request</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingSummary.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>Sem dados de utilização</TableCell></TableRow>
                  ) : billingSummary.map((b) => (
                    <TableRow key={b.apiKeyId} hover sx={{ cursor: 'pointer' }}
                      onClick={() => { setSelectedKeyUsage(b.apiKeyId); }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{b.entityName}</Typography>
                      </TableCell>
                      <TableCell>{b.keyName}</TableCell>
                      <TableCell>
                        <Chip label={b.plan.toUpperCase()} size="small" color={PLAN_COLORS[b.plan] || 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{formatNumber(b.totalRequests)}</Typography>
                      </TableCell>
                      <TableCell align="right">{b.avgResponseTime}ms</TableCell>
                      <TableCell align="right">{formatNumber(b.limits.dailyLimit)}</TableCell>
                      <TableCell align="right">{b.limits.monthlyLimit ? formatNumber(b.limits.monthlyLimit) : 'Ilimitado'}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {b.lastRequest ? new Date(b.lastRequest).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : 'Nunca'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Usage Detail (for selected key) */}
          {(selectedKeyUsage && usageData) && (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                Detalhe de Uso — {keys.find(k => k.id === selectedKeyUsage)?.nome || 'API Key'}
              </Typography>

              {/* KPI Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Hoje</Typography>
                      <Typography variant="h4" fontWeight={700}>{formatNumber(usageData.today)}</Typography>
                      <Typography variant="caption" color="text.secondary">requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Este Mês</Typography>
                      <Typography variant="h4" fontWeight={700}>{formatNumber(usageData.thisMonth)}</Typography>
                      <Typography variant="caption" color="text.secondary">requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Período ({usagePeriod} dias)</Typography>
                      <Typography variant="h4" fontWeight={700}>{formatNumber(usageData.total)}</Typography>
                      <Typography variant="caption" color="text.secondary">requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                {/* Daily Chart (bar chart via LinearProgress) */}
                <Grid size={{ xs: 12, lg: 8 }}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Requests por Dia</Typography>
                    {usageData.daily.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Sem dados no período
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        {usageData.daily.slice(0, 15).map((d) => (
                          <Box key={d.date} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ width: 80, flexShrink: 0, fontFamily: 'monospace' }}>
                              {new Date(d.date).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: '2-digit' })}
                            </Typography>
                            <Box sx={{ flex: 1, mx: 1 }}>
                              <LinearProgress variant="determinate"
                                value={(d.requests / maxDailyRequests) * 100}
                                sx={{ height: 16, borderRadius: 1, bgcolor: 'action.hover',
                                  '& .MuiLinearProgress-bar': { borderRadius: 1 } }} />
                            </Box>
                            <Typography variant="caption" sx={{ width: 50, textAlign: 'right', fontWeight: 600 }}>
                              {d.requests}
                            </Typography>
                            <Typography variant="caption" sx={{ width: 50, textAlign: 'right', color: 'text.secondary' }}>
                              {d.avgResponseTime}ms
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* By Status Code */}
                <Grid size={{ xs: 12, lg: 4 }}>
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Por Status Code</Typography>
                    {usageData.byStatus.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Sem dados</Typography>
                    ) : usageData.byStatus.map((s) => (
                      <Box key={s.statusCode} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Chip label={s.statusCode} size="small" color={getStatusColor(s.statusCode) as any} />
                        <Typography variant="body2" fontWeight={600}>{formatNumber(s.requests)}</Typography>
                      </Box>
                    ))}
                  </Paper>

                  {/* Top Endpoints */}
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Top Endpoints</Typography>
                    {usageData.byEndpoint.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Sem dados</Typography>
                    ) : usageData.byEndpoint.slice(0, 8).map((e, idx) => (
                      <Box key={idx} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Chip label={e.method} size="small" variant="outlined" sx={{ mr: 1, fontFamily: 'monospace', fontSize: 10 }} />
                            <Typography variant="caption" fontFamily="monospace">{e.endpoint.replace('/v1/public/', '/')}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600}>{formatNumber(e.requests)}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Nome *" placeholder="Ex: Integração BrandBoard"
                value={createForm.nome} onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" select label="Entidade *"
                value={createForm.entidadeId} onChange={(e) => setCreateForm({ ...createForm, entidadeId: e.target.value })}>
                <MenuItem value="">Seleccione uma entidade</MenuItem>
                {entities.map((e: any) => <MenuItem key={e.id} value={e.id}>{e.desig}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" select label="Plano"
                value={createForm.plano} onChange={(e) => setCreateForm({ ...createForm, plano: e.target.value })}>
                <MenuItem value="free">Free — 10 req/min, 100/day</MenuItem>
                <MenuItem value="basic">Basic — 60 req/min, 1,000/day</MenuItem>
                <MenuItem value="pro">Pro — 300 req/min, 10,000/day</MenuItem>
                <MenuItem value="enterprise">Enterprise — 1,000 req/min, unlimited</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveCreate} disabled={saving}>
            {saving ? 'A criar...' : 'Criar API Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credOpen} onClose={() => setCredOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>API Key Criada</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Guarde esta key — não será mostrada novamente após fechar este diálogo.
          </Alert>
          <TextField fullWidth label="API Key" value={newApiKey} InputProps={{ readOnly: true }}
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { copyToClipboard(newApiKey); }}>Copiar Key</Button>
          <Button variant="contained" onClick={() => setCredOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Nome" value={editForm.nome}
                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" select label="Plano" value={editForm.plano}
                onChange={(e) => setEditForm({ ...editForm, plano: e.target.value })}>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" select label="Estado" value={editForm.estado}
                onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}>
                <MenuItem value="active">Activa</MenuItem>
                <MenuItem value="suspended">Suspensa</MenuItem>
                <MenuItem value="expired">Expirada</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys;
